import { formatIsbdTitle } from "@biblioteksentralen/cordata";
import { Box, Divider, Flex, HStack, Heading, LinkBox, Text, VStack, useBoolean } from "@biblioteksentralen/react";
import InternalLinkOverlay from "../../../components/InternalLinkOverlay";
import { getPath } from "@libry-content/common";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { Collections } from "../Collections";
import { DocumentTypes } from "../DocumentTypes";
import { HoldingText } from "../HoldingText";
import { ItemCoverImage } from "../ItemCoverImage";
import LabelAndValue from "../LabelAndValue";
import { ListContributors } from "../ListContributors";
import ListRelatedWorks from "../ListRelatedWorks";
import { getDotseparated } from "../getDotseparated";
import { ReservationButton } from "../reservation/ReservationButton";
import { FindInLibraryModal } from "../works/FindInLibraryModal";
import { ManifestationStatusForUser } from "./ManifestationStatusForUser";
import { RefObject } from "react";
import { formatLanguagesList, CordataWorkTriple } from "@libry-content/integrations";
import { useWorkViewContext } from "../works/WorkViewContextProvider";
import { useRediaPlatformContext } from "../../../rediaPlatform/RediaPlatformProvider";
import FindInLibraryButton from "../works/FindInLibraryButton";

type ManifestationCardProps = {
  workTriple: CordataWorkTriple;
  ref: RefObject<HTMLAnchorElement | null> | undefined;
};

export const ManifestationCard = ({ workTriple, ref }: ManifestationCardProps) => {
  const { t } = useTranslation();
  const [findInLibraryModalOpen, { on: openFindInLibraryModal, off: closeFindInLibraryModal }] = useBoolean();
  const { holdingsData, libraries, worksAssociatedWithAggregateManifestations, manifestationStatusForUser } =
    useWorkViewContext();
  const { rediaPlatformIsEnabled } = useRediaPlatformContext();
  const work = workTriple.work;

  const associatedWorks = worksAssociatedWithAggregateManifestations?.[workTriple.manifestationId];

  const workId = workTriple.work.id;
  const languageNames = workTriple.work.isMovie
    ? undefined
    : formatLanguagesList(workTriple.languages, { capitalizeFirstLetter: true });
  const languagesAndYear = getDotseparated([languageNames, workTriple.publicationYear]);
  const collections = workTriple.collections;
  const spokenLanguages = workTriple.getSpokenLanguagesFormatted({ capitalizeFirstLetter: true });
  const externalContentService = workTriple.externalContentService;

  const rawWork = workTriple.work.getRawBmdbWork();
  const manifestation = workTriple.getSortableManifestation();

  return (
    <LinkBox role="group" width="100%" isolation="isolate">
      <HStack justifyContent="space-between">
        <HStack width="100%" display="flex" alignItems="stretch">
          <ItemCoverImage item={workTriple} height="fit-content" minWidth="6rem" maxWidth="6rem" />
          <VStack paddingLeft={{ base: "0", sm: "1rem" }} alignItems="left" justifyContent="space-between">
            <VStack alignItems="left" spacing="0.125rem">
              {workTriple.documentType && <DocumentTypes documentTypes={[workTriple.documentType]} />}
              <InternalLinkOverlay
                _groupHover={{ textDecoration: "underline" }}
                href={getPath({ ...manifestation, workId })}
                ref={ref}
              >
                <Heading marginTop="0.25rem" as="h3" fontSize="lg">
                  {formatIsbdTitle(manifestation)}
                </Heading>
              </InternalLinkOverlay>

              <Text fontSize="sm" lineHeight="1">
                {languagesAndYear}
              </Text>
              <ListManifestationContributors workTriple={workTriple} />
              {!!spokenLanguages && (
                <LabelAndValue fontSize="sm" label={t("Talespråk")} marginTop="0">
                  {spokenLanguages}
                </LabelAndValue>
              )}
              {externalContentService && (
                <LabelAndValue fontSize="sm" label={t("Leverandør")} marginTop="0">
                  {externalContentService.originName}
                </LabelAndValue>
              )}
              {associatedWorks && associatedWorks.length > 0 && (
                <ListRelatedWorks associatedWorks={associatedWorks} representativeManifestation={manifestation} />
              )}
              <Collections fontSize="sm" collections={collections} />
            </VStack>
            <ManifestationStatusForUser
              manifestationStatusForUser={manifestationStatusForUser}
              documentType={manifestation.documentType}
              marginTop="0.25rem"
            />
            <HoldingText
              work={work}
              workTriples={[workTriple]}
              holdings={holdingsData?.holdings}
              manifestationStatusForUser={manifestationStatusForUser}
            />
          </VStack>
        </HStack>
        <VStack spacing="0.75rem" alignItems="right">
          {manifestationStatusForUser && (
            <ReservationButton
              work={rawWork}
              manifestations={[manifestation]}
              manifestationStatusForUser={manifestationStatusForUser}
              externalContentService={externalContentService}
              representativeManifestation={manifestation}
              documentType={manifestation.documentType}
              isDisabled={manifestationStatusForUser.isReservedByUser || manifestationStatusForUser.isLoanedByUser}
              display={{ base: "none", md: "flex" }}
              flex="1"
              margin="0 auto"
              minHeight="2.5rem"
              //width differs because of different button text length. But this is maybe not a good way to do it?
              width={rediaPlatformIsEnabled ? "9rem" : "11rem"}
              hiddenWhenDisabled
              fontSize="md"
            />
          )}
          <FindInLibraryButton
            variant="secondary"
            colorScheme="black"
            width="9rem"
            padding={{ base: "0 1rem", md: "0 3rem" }}
            margin={{ base: "0", md: "0 2rem 0 2rem" }}
            display={{ base: "none", md: "flex" }}
            openFindInLibraryModal={openFindInLibraryModal}
          />
        </VStack>
      </HStack>
      <Flex flexWrap="wrap-reverse" justifyContent="center" gap="0.75rem" marginTop="0.75rem">
        <FindInLibraryButton
          flex="1"
          minWidth="9rem"
          variant="secondary"
          colorScheme="black"
          size="md"
          display={{ base: "initial", md: "none" }}
          openFindInLibraryModal={openFindInLibraryModal}
        />
        {manifestationStatusForUser && (
          <ReservationButton
            work={rawWork}
            manifestations={[manifestation]}
            libraries={libraries}
            manifestationStatusForUser={manifestationStatusForUser}
            externalContentService={externalContentService}
            representativeManifestation={manifestation}
            documentType={manifestation.documentType}
            display={{ base: "flex", md: "none" }}
            minWidth="9rem"
            fontSize="md"
            hiddenWhenDisabled
            {...(rediaPlatformIsEnabled ? { flex: "1" } : { width: "11rem" })}
          />
        )}
      </Flex>

      <Divider marginTop="1rem"></Divider>
      <FindInLibraryModal
        isOpen={findInLibraryModalOpen}
        onClose={closeFindInLibraryModal}
        representativeManifestation={manifestation}
        manifestationIds={[manifestation.id]}
        work={work}
        libraries={libraries}
      />
    </LinkBox>
  );
};

const ListManifestationContributors = ({ workTriple }: { workTriple: CordataWorkTriple }) => {
  const { format = "" } = workTriple.documentType ?? {};
  const illustrators = workTriple.getContributorsWithRoles(["illustr."]);
  const translators = !["Lydbok", "E-lydbok"].includes(format) ? workTriple.getContributorsWithRoles(["overs."]) : [];
  const narrators = ["Lydbok", "E-lydbok"].includes(format) ? workTriple.getContributorsWithRoles(["innl."]) : [];

  const conductors = workTriple.getContributorsWithRoles(["dir."]);
  const performers = workTriple.getContributorsWithRoles(["utøv."]);

  if ([...translators, ...narrators, ...illustrators, ...conductors, ...performers].length === 0) return null;

  return (
    <Box paddingTop="0.2rem" fontSize="sm">
      {translators.length > 0 && <ListContributors contributors={translators} />}
      {narrators.length > 0 && <ListContributors contributors={narrators} />}
      {illustrators.length > 0 && <ListContributors contributors={illustrators} />}
      {conductors.length > 0 && <ListContributors contributors={conductors} />}
      {performers.length > 0 && <ListContributors contributors={performers} />}
    </Box>
  );
};
